import styled from '@emotion/styled';
import Slider from 'react-slick';
import { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import 'slick-carousel/slick/slick.css';
import { useTranslation } from 'react-i18next';
import { IconButton, Stack, Text, tokens } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { Tile } from '@hotjar/activation-toolkit';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { useSuggestions } from './useSuggestions';
import type { Suggestion } from './useSuggestions';
import { FullContentWidth } from '../../Overview';

const SINGLE_TILE_WIDTH = 190 + 32;

const Container = styled(Stack)`
  position: relative;
  margin-bottom: ${tokens.spacer.x3};

  min-width: 0;
`;

const StyledStack = styled(Stack)`
  overflow: hidden;
  padding: ${tokens.spacer.x2pt5} 0;
`;

const PrevButton = styled(IconButton)`
  & button {
    position: absolute;
    top: 20px;
    left: 0;
    z-index: 1;
    width: 40px;
    height: calc(100% - 40px);
    background: linear-gradient(90deg, ${tokens.color.white} 0%, rgba(255, 255, 255, 0) 45%);
    border: none;
    transition: all 0.24s;
    opacity: ${({ isVisible }: { isVisible: boolean }) => (isVisible ? 1 : 0)};
    visibility: ${({ isVisible }: { isVisible: boolean }) => (isVisible ? 'visible' : 'hidden')};
  }

  & button:hover {
    background: rgba(121, 125, 141, 0.08);
  }
`;

const NextButton = styled(PrevButton)`
  & button {
    left: 100%;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${tokens.color.white} 45%);
    transform: translateX(-100%);
    opacity: ${({ isVisible }: { isVisible: boolean }) => (isVisible ? 1 : 0)};
    visibility: ${({ isVisible }: { isVisible: boolean }) => (isVisible ? 'visible' : 'hidden')};
  }
`;

const PrevArrow = ({
  onClick,
  isVisible,
}: {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isVisible: boolean;
}) => (
  <PrevButton
    kind="ghost"
    onClick={onClick}
    icon="chevron_left"
    tooltipText="Previous"
    isVisible={isVisible}
  />
);

const NextArrow = ({
  onClick,
  isVisible,
}: {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isVisible: boolean;
}) => (
  <NextButton
    kind="ghost"
    onClick={onClick}
    icon="chevron_right"
    tooltipText="Next"
    isVisible={isVisible}
  />
);

export const SuggestionsBox = ({ site }: { site: Site }): JSX.Element | null => {
  const { t } = useTranslation();
  const { suggestions, dismiss } = useSuggestions(site);
  const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(2);
  const { click, viewElement } = useTrack();
  const trackClick = (element: string) => click(element, 'Suggestion Tiles', {});
  const styledStackRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [isSliderVisible, setIsSliderVisible] = useState(false);
  const onTileClick = (
    tile: Suggestion,
    // eslint-disable-next-line @typescript-eslint/default-param-last
    silent = false,
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (!(tile.isExternalLink ?? false)) {
      e.preventDefault();
      browserHistory.push(tile.href, { replace: false });
    }
    trackClick(tile.id);
    dismiss(tile.id, silent);
  };
  const onTileDismiss = (tile: string) => {
    trackClick(`${tile} Dismiss`);
    dismiss(tile);
  };

  const handleElementViewed = (element: string) => {
    viewElement(element, 'Suggestion Tiles', {});
  };

  useEffect(() => {
    const mainContentWidth = styledStackRef.current?.clientWidth || 0;
    const isTilesScrollable = mainContentWidth <= SINGLE_TILE_WIDTH * suggestions.length;

    if (isTilesScrollable !== isSliderVisible) {
      setIsSliderVisible(isTilesScrollable);
    }
  }, [width, suggestions, setIsSliderVisible, isSliderVisible]);

  useEffect(() => {
    const mainContentWidth = styledStackRef.current?.clientWidth || 0;
    setSlidesToShow(Math.round(mainContentWidth / SINGLE_TILE_WIDTH));
  }, [width]);

  if (suggestions.length === 0) return null;

  return (
    <FullContentWidth>
      <Container direction="column" ref={styledStackRef}>
        <Text as="h2" textType="header5">
          {t('activation:space-overview.suggestions.title')}
        </Text>
        {isSliderVisible ? (
          <div>
            <Slider
              dots={false}
              infinite={false}
              speed={500}
              slidesToShow={slidesToShow}
              slidesToScroll={2}
              beforeChange={(_oldIndex, newIndex) => setCurrentSliderIndex(newIndex)}
              prevArrow={<PrevArrow isVisible={currentSliderIndex > 0} />}
              nextArrow={<NextArrow isVisible={currentSliderIndex < suggestions.length} />}
            >
              {suggestions.map((suggestion) => (
                <Tile
                  key={suggestion.id}
                  id={suggestion.id}
                  title={suggestion.title}
                  description={suggestion.description}
                  href={suggestion.href}
                  image={suggestion.image}
                  isExternalLink={suggestion.isExternalLink}
                  onClick={(e) => onTileClick(suggestion, true, e)}
                  onDismiss={() => onTileDismiss(suggestion.id)}
                  onView={handleElementViewed}
                />
              ))}
            </Slider>
          </div>
        ) : (
          <StyledStack direction="row" spacing="x3">
            {suggestions.map((suggestion) => (
              <Tile
                key={suggestion.id}
                id={suggestion.id}
                title={suggestion.title}
                description={suggestion.description}
                href={suggestion.href}
                image={suggestion.image}
                isExternalLink={suggestion.isExternalLink}
                onClick={(e) => onTileClick(suggestion, true, e)}
                onDismiss={() => onTileDismiss(suggestion.id)}
                onView={handleElementViewed}
              />
            ))}
          </StyledStack>
        )}
      </Container>
    </FullContentWidth>
  );
};
